import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Text,
  Spacer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
  useTheme,
  useColorModeValue,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Spinner
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { cqAPI } from "../../lib/Auth";

export default function MessagesTemplatesModal({ isOpen, onClose, onOpen, handleSelect }) {
  const toast = useToast();
  const [selectedId, setSelectedId] = useState(null);
  const [selectedBody, setSelectedBody] = useState("");
  const [editingTemplate, setEditingTemplate] = useState({});
  const [messageTemplates, setMessageTemplates] = useState([]);

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const fetchMessageTemplates = async () => {
    try {
      setSelectedId(null);
      setSelectedBody("");
      setEditingTemplate({});
      const response = await cqAPI.get("/messages-templates");
      if (response.type === 'ok') {
        setMessageTemplates(response.data);
      } else if (response.type === 'error') {
        return toast({
          title: "Error",
          description: "Something went wrong.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.log(error);
      return toast({
        title: "Error",
        description: "Something went wrong.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }

  useEffect(() => {
    fetchMessageTemplates();
    setSelectedId(null);
    setSelectedBody("");
    setEditingTemplate({});
  }, [isOpen])


  const handleDelete = async (template) => {
    try {
      const response = await cqAPI.del(`/messages-templates?id=${template._id}`);
      if (response.type === 'ok') {
        toast({
          title: "Success",
          description: response.msg || "Template deleted successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (response.type === 'error') {
        return toast({
          title: "Error",
          description: response.msg || "Something went wrong.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      await fetchMessageTemplates();
    } catch (error) {
      console.log(error);
      return toast({
        title: "Error",
        description: "Something went wrong.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }


  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>Message templates</ModalHeader>
          <ModalCloseButton/>

          <ModalBody>
            <Button
              variant="solid"
              mb={4}
              onClick={() => {
                setEditingTemplate({})
                onClose();
                onEditOpen();
              }}>
              Create Template
            </Button>

            <Box border="1px solid" borderColor="gray.200" rounded="md" overflow="hidden">
              <Box height="20vh" overflowY="auto">
                {messageTemplates.map((t) => (
                  <TemplateRow
                    key={t._id}
                    template={t}
                    selected={selectedId === t._id}
                    onSelect={() => {
                      setSelectedId(t._id)
                      setSelectedBody(t.body)
                    }}
                    onEdit={() => {
                      setEditingTemplate(t);
                      onClose()
                      onEditOpen();
                    }}
                    onDelete={() => {
                      handleDelete(t)
                    }}
                  />
                ))}
              </Box>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button variant="solid" isDisabled={!selectedId} onClick={() => {
              handleSelect(selectedBody)
              onClose()
            }}>
              Select
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <EditTemplate
        isOpen={isEditOpen}
        onClose={() => {
          onOpen();
          onEditClose();
        }}
        template={editingTemplate}
      />
    </>
  );
}

function TemplateRow({ template, selected, onSelect, onEdit, onDelete }) {
  const theme = useTheme();
  const hoverBgColor = useColorModeValue(theme.colors.gray[50], theme.colors.gray[700])
  const selectedBg = useColorModeValue(theme.colors.gray[100], theme.colors.gray[800]);

  return (
    <Flex
      role="group"
      align="center"
      gap={3}
      px={3}
      py={2.5}
      cursor="pointer"
      _hover={{ bg: hoverBgColor }}
      bg={selected ? selectedBg : "transparent"}
      onClick={onSelect}
    >
      <Text noOfLines={1} fontWeight={selected ? "medium" : "normal"}>
        {template.title}
      </Text>

      <Spacer/>

      <Menu isLazy placement="bottom-end">
        <MenuButton
          as={IconButton}
          aria-label="More options"
          icon={<FontAwesomeIcon icon={faEllipsisV}/>}
          variant="ellipsis"
          size="sm"
          opacity={{ base: 1, md: 0 }}
          pointerEvents={{ base: "auto", md: "none" }}
          _groupHover={{ opacity: 1, pointerEvents: "auto" }}
          _groupFocusWithin={{ opacity: 1, pointerEvents: "auto" }}
          // _expanded={{ opacity: 1, bg: "gray.100" }}
          transition="opacity 0.15s ease"
          onClick={(e) => e.stopPropagation()}
        />
        <MenuList>
          <MenuItem onClick={() => {
            onEdit();
          }}>Edit</MenuItem>
          <MenuItem color="red" onClick={onDelete}>Delete</MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
}

function EditTemplate({ isOpen, onClose, template = {} }) {
  const [title, setTitle] = useState(template.title || "");
  const [body, setBody] = useState(template.body || "");
  const [id, setId] = useState(template._id || null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const clearFields = () => {
    setTitle("");
    setBody("");
    setId(null);
  }

  useEffect(() => {
    setTitle(template.title || "");
    setBody(template.body || "");
    setId(template._id || null);
  }, [isOpen])

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      let response;
      if (id) {
        response = await cqAPI.put(`/messages-templates?id=${id}`, { title, body, _id: id });
      } else {
        response = await cqAPI.post("/messages-templates", { title, body });
      }
      setSubmitting(false);
      if (response.type === 'ok') {
        toast({
          title: "Success",
          description: response.msg || "Template saved successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (response.type === 'error') {
        return toast({
          title: "Error",
          description: response.msg || "Something went wrong.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      onClose();
    } catch (error) {
      console.log(error);
      setSubmitting(false);
      return toast({
        title: "Error",
        description: "Something went wrong.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>{id ? "Edit" : "Create"} template</ModalHeader>
        <ModalCloseButton onClick={clearFields}/>

        <ModalBody>
          <FormControl isRequired>
            <FormLabel>Template Title</FormLabel>
            <Input type='text' value={title} onChange={(e) => setTitle(e.target.value)}/>
          </FormControl>
          <FormControl isRequired mt={4}>
            <FormLabel>Template Body</FormLabel>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button isDisabled={submitting} variant="ghost" mr={2} onClick={() => {
            onClose()
            clearFields()
          }}>
            Cancel
          </Button>
          <Button variant="solid" type="submit" isDisabled={submitting} onClick={handleSubmit}>
            {submitting ? <Spinner/> : "Save"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}